/* eslint-disable react/require-default-props */
/* eslint-disable no-alert, react/jsx-no-bind, react/no-multi-comp*/
import React, {useState} from "react";
import AddressInfo from "./AddressInfo";
import BillingInfo from "./BillingInfo";
import CartAPI from "../../shared/modules/CartAPI";
import CartHeader from "./CartHeader";
import CartHelper from "../../shared/modules/CartHelper";
import CartItemCard from "./CartItemCard";
import EmptyCart from "./EmptyCart";
import LoadingSpinner from "../BuyItNow/components/LoadingSpinner";
import PromoCodeField from "./PromoCodeField";
import PropTypes from "prop-types";
import Summary from "./Summary";

const Cart = ({
  billingInfo,
  cartInfo,
  clearanceItemAvailability,
  handleCloseCartModal,
  handleDisplayPurchaseNotification,
  loadingPage,
  retailItemAvailability,
  setCartInfo,
  setNotificationText,
  setIsBillingEdit,
  setIsShippingEdit,
  shippingInfo,
  promoCode,
  setPromoCode
}) => {
  if (loadingPage) return (
    <div className="Cart">
      <CartHeader
        classes="justify-between items-center text-2xl"
        handleCloseCartModal={handleCloseCartModal}
        showCloseButton
      />
      loading your shopping cart...
    </div>
  );

  const [
    loading,
    setLoading
  ] = useState(false);

  const addUnavailableClass = (id) => {
    const element = document.getElementById(id);

    if (element) {
      element.classList.add("unavailable-item");
      const listener = element.addEventListener("click", () => {
        element.classList.remove("unavailable-item");
        element.removeEventListener("click", listener);
      });
    }
  };

  const handlePurchaseError = (response) => {
    const unavailableClearancePds = response.clearance_unavailable_pds;
    const unavailableRetailPds = response.retail_unavailable_pds;

    if (unavailableClearancePds?.length > 0) {
      unavailableClearancePds.forEach((pd) => {
        addUnavailableClass(`${pd.id}-clearance`);
      });
    }

    if (unavailableRetailPds?.length > 0) {
      unavailableRetailPds.forEach((pd) => {
        addUnavailableClass(`${pd.id}-retail`);
      });
    }
    setNotificationText(response.error);
  };

  const hasValidAddressInfo = () => CartHelper.hasValidAddressInfo(shippingInfo);

  const hasValidBillingInfo = () => CartHelper.hasBillingInfo(billingInfo);

  const getButtonText = () => {
    if (!hasValidAddressInfo()) {
      return "Enter Address";
    }
    if (!hasValidBillingInfo()) {
      return "Enter Billing Info";
    }
    return "Place Order";
  };

  const handlePurchase = () => {
    if (!hasValidAddressInfo()) {
      setNotificationText("Please enter shipping information");
      return;
    }
    if (!hasValidBillingInfo()) {
      setNotificationText("Please enter billing information");
      return;
    }
    setLoading(true);
    CartAPI.purchaseCart(promoCode).then((response) => {
      if (response.error) {
        handlePurchaseError(response);
        setLoading(false);
      } else {
        handleDisplayPurchaseNotification();
        handleCloseCartModal();
      }
    });
  };

  const handleClick = () => {
    if (!hasValidAddressInfo()) {
      setIsShippingEdit(true);
      return;
    }
    if (!hasValidBillingInfo()) {
      setIsBillingEdit(true);
      return;
    }
    handlePurchase();
  };

  const getCartItemCount = () => {
    if (!cartInfo) return "";

    const {"cart_items_by_sku_and_kind": {"retail_items": retailItems, "clearance_items": clearanceItems}} = cartInfo;
    let count = 0;

    retailItems.forEach((item) => {
      count += item.quantity;
    });

    clearanceItems.forEach((item) => {
      count += item.quantity;
    });

    return count;
  };

  const mapCartItems = (items, itemAvailability, storeKind) => items.map((item) => {
    const countAvailable = itemAvailability[item.product_descriptor.id];

    return (
      <CartItemCard
        countAvailable={countAvailable}
        id={`${item.product_descriptor.id}-${storeKind}`}
        itemInfo={item}
        key={`${item.product_descriptor.id}-${storeKind}`}
        promoCode={promoCode}
        setCartInfo={setCartInfo}
        setNotificationText={setNotificationText}
        storeKind={storeKind}
      />
    );
  });

  const renderCartInfo = () => {
    const {"cart_items_by_sku_and_kind": {"retail_items": retailItems, "clearance_items": clearanceItems}} = cartInfo;

    const retailItemCards = mapCartItems(retailItems, retailItemAvailability, "retail");
    const clearanceItemCards = mapCartItems(clearanceItems, clearanceItemAvailability, "clearance");

    return (
      <div className="cart-info">
        {retailItemCards}
        {clearanceItemCards}
        <AddressInfo
          addressInfo={shippingInfo}
          isShipping
          setIsAddressEdit={setIsShippingEdit}
        />
        <BillingInfo
          billingInfo={billingInfo}
          setIsBillingEdit={setIsBillingEdit}
        />
        <Summary cartInfo={cartInfo} />

        <PromoCodeField
          cartInfo={cartInfo}
          promoCode={promoCode}
          setCartInfo={setCartInfo}
          setPromoCode={setPromoCode}
        />
        <div className="flex flex-col gap-2">
          <button
            className="btn btn-primary cart-save-button button-primary"
            disabled={loading}
            onClick={handleClick}
            type="button"
          >
            {loading ? <LoadingSpinner size={26} /> : getButtonText()}
          </button>
          <a
            className="button-outlined-white"
            href="/jewelry"
          >
            Keep Shopping
          </a>
        </div>
      </div>
    );
  };

  const renderEmptyCart = () => (
    <EmptyCart handleCloseCartModal={handleCloseCartModal} />
  );

  return (
    <div className="Cart">
      <CartHeader
        classes="justify-between items-center text-2xl"
        handleCloseCartModal={handleCloseCartModal}
        itemCount={getCartItemCount()}
        showCloseButton
      />
      {cartInfo ? renderCartInfo() : renderEmptyCart()}

    </div>
  );
};

Cart.propTypes = {
  "billingInfo": PropTypes.object,
  "cartInfo": PropTypes.object,
  "clearanceItemAvailability": PropTypes.object,
  "handleCloseCartModal": PropTypes.func.isRequired,
  "handleDisplayPurchaseNotification": PropTypes.func.isRequired,
  "loadingPage": PropTypes.bool.isRequired,
  "promoCode": PropTypes.string.isRequired,
  "retailItemAvailability": PropTypes.object,
  "setCartInfo": PropTypes.func.isRequired,
  "setIsBillingEdit": PropTypes.func.isRequired,
  "setIsShippingEdit": PropTypes.func.isRequired,
  "setNotificationText": PropTypes.func.isRequired,
  "setPromoCode": PropTypes.func.isRequired,
  "shippingInfo": PropTypes.object
};

Cart.defaultProps = {
  "billingInfo": null,
  "cartInfo": null,
  "shippingInfo": null
};

export default Cart;
